import React, { FC, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import BasicInput from '../../../../Inputs/BaseInput';
import FormFooter from '../../../../FormFooter/FormFooter';
import CancelModal from '../../../../CancelModal/CancelModal';
import useUpdateCompany from '../../../../../hooks/useUpdateCompany';
import ImageCropperDialog from '../../../../ImageCropperDialog/ImageCropperDialog';
import CompanySettingsToggle from './CompanySettingsToggle';

interface Props {
  companyId: number;
  companyName: string;
  companyUrl: string;
  companyLogo: string;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
}
const UpdateCompanyForm: FC<Props> = ({
  companyId,
  companyName,
  companyUrl,
  companyLogo,
  setModal,
}) => {
  const {
    name,
    url,
    handleSubmit,
    t,
    errors,
    onSubmit,
    saveState,
    showCancel,
    handleCancelModal,
    handleCancel,
    isDisabled,
    handleLogoChange,
  } = useUpdateCompany(companyId, companyName, companyUrl, setModal);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedImage: Blob) => {
    handleLogoChange(croppedImage);
    setLogoPreview(URL.createObjectURL(croppedImage));
    setShowCropper(false);
    setSelectedImage(null);
  };

  const handleRemoveLogo = () => {
    handleLogoChange(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BasicInput
        type="text"
        onChange={name.onChange}
        onBlur={name.onBlur}
        refProp={name.ref}
        name={name.name}
        label={t('CompaniesTable.Name')}
        isRequired
        errorText={errors?.Name?.message?.toString() || ''}
      />

      <div className="mt-4">
        <BasicInput
          type="text"
          onChange={url.onChange}
          onBlur={url.onBlur}
          refProp={url.ref}
          name={url.name}
          label={t('CompaniesTable.Url')}
          errorText={errors?.Url?.message?.toString() || ''}
        />
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <label className="text-sm font-medium">
          {t('CompaniesTable.Logo')}
        </label>

        {logoPreview && (
          <div className="relative w-[120px] h-[120px] rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50">
            <img
              src={logoPreview}
              alt="Company logo preview"
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white border-none text-2xl leading-none flex items-center justify-center cursor-pointer transition-colors"
              onClick={handleRemoveLogo}
            >
              ×
            </button>
          </div>
        )}

        <div className="mt-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
            id="logo-upload"
          />
          <Button
            label={
              logoPreview || companyLogo
                ? t('CompaniesTable.ChangeLogo')
                : t('CompaniesTable.UploadLogo')
            }
            icon="pi pi-upload"
            onClick={() => fileInputRef.current?.click()}
            text
            className="text-brand-darkgrey"
            type="button"
          />
        </div>
      </div>

      {/* Saves on its own (staff-only endpoint), not with the profile fields below */}
      <CompanySettingsToggle companyId={companyId} />

      <div style={{ marginTop: '1rem' }}>
        <FormFooter
          translate={t}
          saveState={saveState}
          submit
          onClickCancel={handleCancel}
          disabled={isDisabled}
        />
      </div>
      {showCancel && handleCancelModal && (
        <CancelModal onClickCancelModal={handleCancelModal} />
      )}

      {showCropper && selectedImage && (
        <ImageCropperDialog
          image={selectedImage}
          onComplete={handleCropComplete}
          onClose={() => {
            setShowCropper(false);
            setSelectedImage(null);
          }}
        />
      )}
    </form>
  );
};

export default UpdateCompanyForm;
